import * as Dialog from '@studio/design-system/dialog';
import { useComposedRefs } from '@studio/react-utils/hooks';
import type { ReactElement } from 'react';
import { forwardRef, useRef } from 'react';
import { Context } from './context.js';
import { Scaffold } from './scaffold.jsx';
import type { Props as ProviderProps } from './store/index.js';
import { Provider } from './store/index.js';
import type { Props } from './types.js';
import { useWorkflow } from './wiring.js';

const Workflow = forwardRef<Dialog.Api, Omit<Props, keyof ProviderProps>>(
  (props, forwardedRef): ReactElement => {
    const apiRef = useRef<Dialog.Api>(null);
    const wiring = useWorkflow(apiRef);

    return (
      <Dialog.Root ref={useComposedRefs(apiRef, forwardedRef)} {...props}>
        <Dialog.Overlay>
          {/* Dialog.Content's closeOnEscape default is inverted (undefined
              → preventDefault via shortCircuit at design-system/dialog/content.tsx:25).
              Opt in explicitly so Escape dismisses the settings sheet.
              Per rule: reference-rules-in-comments (ai.knowledge/global) — design-system
              default flip is a separate follow-up (other consumers may depend on it). */}
          <Dialog.Sheet closeOnEscape>
            <Context.Provider value={wiring}>
              <Scaffold />
            </Context.Provider>
          </Dialog.Sheet>
        </Dialog.Overlay>
      </Dialog.Root>
    );
  }
);

/** Account `<Settings>` component. */
export const Settings = forwardRef<Dialog.Api, Props>(
  (
    {
      account,
      defaultPanel,
      defaultTopLevelPanel,
      initialPlanPickerRoomId,
      hotKeys,
      organizations,
      overrideOrganizationId,
      overrideRoomId,
      rooms,
      user,
      ...props
    },
    forwardedRef
  ): ReactElement => (
    <Provider
      account={account}
      defaultPanel={defaultPanel}
      defaultTopLevelPanel={defaultTopLevelPanel}
      initialPlanPickerRoomId={initialPlanPickerRoomId}
      hotKeys={hotKeys}
      organizations={organizations}
      overrideOrganizationId={overrideOrganizationId}
      overrideRoomId={overrideRoomId}
      rooms={rooms}
      user={user}
    >
      <Workflow ref={forwardedRef} {...props} />
    </Provider>
  )
);
